/**
 * The past-runs date-range picker, capped at this connection's own run retention.
 *
 * The cap is the product rule, not a convenience: runs older than retention do not exist, and the
 * spec is explicit that we must not "design a surface that implies deeper history than exists." So
 * the calendar is bounded on BOTH axes — `disabled` stops an out-of-window day being clicked and
 * `startMonth`/`endMonth` stops the user navigating to a month where every day would be dead.
 * `usePastRuns.clampPastRunsRange` enforces the same window in data, so a range that somehow gets
 * past this control still cannot ask the platform for history it does not keep. `retentionDays` is
 * REQUIRED, not defaulted — `retentionDays(sync)` (`lib/run-view.ts`) is the one place a missing
 * value legitimately becomes the platform default, and every caller here already has that resolved
 * number.
 *
 * There is no shared range picker in this repo; the only other assembled one is private to
 * `dashboard/components/DashboardParamBar.tsx`, whose draft-selection handling this mirrors.
 */
import dayjs from 'dayjs';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Calendar } from '@/_shared/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';

import { clampPastRunsRange, type PastRunsRange } from '../hooks/usePastRuns';

/**
 * Nothing longer than retention is offered, because nothing longer than retention exists. Built from
 * the CONNECTION's own retention, not the platform constant — a 14-day connection must not dangle a
 * "Last 30 days" preset whose tail end the platform has already forgotten.
 *
 * Filtered AND deduped rather than a fixed [1, 7, retentionDays] triple: at `retentionDays === 7`
 * (the wizard's own advertised minimum, `ActivationSyncSchedule.tsx`'s `min={7}` — though the backend
 * accepts any positive integer, so a shorter connection is reachable too) the naive triple produces
 * two buttons both labelled AND keyed "Last 7 days"; below 7 it offered "Last 7 days" on a connection
 * that keeps less, contradicting this very comment. `1` (as "Last 24 hours") always survives — a
 * connection retaining less than a day is not a real case this surface needs to special-case for.
 */
function buildPresets(retentionDays: number): { label: string; days: number }[] {
  const days = [...new Set([1, 7, retentionDays].filter((d) => d <= retentionDays))];
  return days.map((d) => ({ label: d === 1 ? 'Last 24 hours' : `Last ${d} days`, days: d }));
}

interface PastRunsRangePickerProps {
  range: PastRunsRange;
  onChange: (range: PastRunsRange) => void;
  disabled?: boolean;
  /**
   * This connection's retention — see `retentionDays()` in `lib/run-view.ts`. REQUIRED, not
   * defaulted: `retentionDays(sync)` is the one place a missing value legitimately becomes the
   * platform default, so a future caller here cannot forget it and silently offer 30-day presets on a
   * connection that keeps less.
   *
   * `null` is that function's own distinct value — the connection has turned run-log retention OFF —
   * and it is passed through here UNCHANGED rather than being collapsed to the platform default the
   * way the panel's query window has to be. The QUERY has to ask for some window; the CONTROL does not
   * have to offer one.
   */
  retentionDays: number | null;
}

function label(range: PastRunsRange): string {
  const from = dayjs(range.from);
  const to = dayjs(range.to);
  if (from.isSame(to, 'day')) return from.format('MMM D YYYY');
  return `${from.format('MMM D')} – ${to.format('MMM D YYYY')}`;
}

export function PastRunsRangePicker({
  range,
  onChange,
  disabled = false,
  retentionDays,
}: PastRunsRangePickerProps): React.ReactElement {
  // A half-finished selection (from picked, to not yet) is local: committing it would refetch the
  // table against a range the user has not finished expressing.
  const [draft, setDraft] = useState<DateRange | null>(null);

  function commit(from: dayjs.Dayjs, to: dayjs.Dayjs): void {
    setDraft(null);
    onChange({ from: from.startOf('day').toDate(), to: to.endOf('day').toDate() });
  }

  function handleSelect(next: DateRange | undefined): void {
    if (!next) {
      setDraft(null);
      return;
    }
    if (next.from && next.to) {
      commit(dayjs(next.from), dayjs(next.to));
      return;
    }
    setDraft(next);
  }

  // Retention OFF. `RunsPanel` still has to query SOMETHING — there is no disabled query shape, so
  // it falls back to `RUN_RETENTION_DAYS` for the window — but that fallback is a query detail, and
  // offering it as "Last 30 days" beside a sentence reading "Run logs are not retained for this
  // connection." is the control contradicting the copy next to it. No window is honest here, including
  // "Last 24 hours", so none is offered.
  //
  // Disabled with a reason, not hidden (`ui` skill rule 10): the range this table is showing is still
  // a fact worth stating, and a control that vanishes on some connections and not others leaves the
  // reader to guess which. The label keeps saying which window is on screen; the tooltip says why it
  // cannot be changed.
  if (retentionDays === null) {
    return (
      <ActionTooltip show message="This connection does not retain run logs, so there is no history window to choose.">
        <Button
          variant="secondary"
          size="sm"
          disabled
          aria-label={`Date range ${label(range)}. Not changeable: this connection does not retain run logs`}
          Icon={<CalendarIcon aria-hidden />}
        >
          {label(range)}
        </Button>
      </ActionTooltip>
    );
  }

  const presets = buildPresets(retentionDays);

  // Derived from the same expression the hook clamps with, so the calendar never offers a day the
  // data layer would then silently pull back.
  const minDate = clampPastRunsRange({ from: new Date(0), to: new Date() }, retentionDays).from;
  const maxDate = dayjs().endOf('day').toDate();
  const selected: DateRange = draft ?? { from: range.from, to: range.to };

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) setDraft(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled}
          aria-label={`Change date range, currently ${label(range)}`}
          Icon={<CalendarIcon aria-hidden />}
        >
          {label(range)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="w-32 space-y-1 border-r border-border p-2">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="tertiary"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  commit(dayjs().subtract(preset.days - 1, 'day'), dayjs());
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <Calendar
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={draft?.from ?? range.from}
            disabled={{ before: minDate, after: maxDate }}
            startMonth={minDate}
            endMonth={maxDate}
            showOutsideDays={false}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
